'use client';

import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface BtTrade {
  id: string;
  date: string;
  symbol: string;
  direction: 'Long' | 'Short';
  entry: number;
  exit: number;
  pnl: number;
  rr: number;
  patternScore: number;
  bars: number;
  outcome: 'Win' | 'Loss';
}

const btTrades: BtTrade[] = [
  { id: 'bt-001', date: 'Jan 8, 2025', symbol: 'NVDA', direction: 'Long', entry: 128.40, exit: 136.20, pnl: 1170, rr: 3.1, patternScore: 88, bars: 24, outcome: 'Win' },
  { id: 'bt-002', date: 'Jan 15, 2025', symbol: 'NVDA', direction: 'Long', entry: 132.80, exit: 129.40, pnl: -510, rr: -0.9, patternScore: 72, bars: 8, outcome: 'Loss' },
  { id: 'bt-003', date: 'Jan 24, 2025', symbol: 'NVDA', direction: 'Long', entry: 131.20, exit: 138.80, pnl: 1140, rr: 2.8, patternScore: 91, bars: 31, outcome: 'Win' },
  { id: 'bt-004', date: 'Feb 5, 2025', symbol: 'NVDA', direction: 'Short', entry: 142.60, exit: 136.40, pnl: 930, rr: 2.4, patternScore: 79, bars: 18, outcome: 'Win' },
  { id: 'bt-005', date: 'Feb 18, 2025', symbol: 'NVDA', direction: 'Long', entry: 138.20, exit: 133.80, pnl: -660, rr: -1.1, patternScore: 71, bars: 6, outcome: 'Loss' },
  { id: 'bt-006', date: 'Mar 3, 2025', symbol: 'NVDA', direction: 'Long', entry: 134.60, exit: 142.40, pnl: 1170, rr: 3.0, patternScore: 85, bars: 28, outcome: 'Win' },
  { id: 'bt-007', date: 'Mar 14, 2025', symbol: 'NVDA', direction: 'Long', entry: 139.80, exit: 136.20, pnl: -540, rr: -0.8, patternScore: 73, bars: 11, outcome: 'Loss' },
  { id: 'bt-008', date: 'Mar 28, 2025', symbol: 'NVDA', direction: 'Short', entry: 148.40, exit: 141.20, pnl: 1080, rr: 2.7, patternScore: 82, bars: 22, outcome: 'Win' },
  { id: 'bt-009', date: 'Apr 9, 2025', symbol: 'NVDA', direction: 'Long', entry: 143.60, exit: 151.80, pnl: 1230, rr: 3.2, patternScore: 93, bars: 35, outcome: 'Win' },
  { id: 'bt-010', date: 'Apr 22, 2025', symbol: 'NVDA', direction: 'Long', entry: 148.20, exit: 144.60, pnl: -540, rr: -0.9, patternScore: 70, bars: 9, outcome: 'Loss' },
];

export default function BacktestTradeList() {
  const [expanded, setExpanded] = useState(false);
  const [sortKey, setSortKey] = useState<keyof BtTrade>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const sorted = [...btTrades].sort((a, b) => {
    const aVal = a[sortKey];
    const bVal = b[sortKey];
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    }
    return sortDir === 'asc'
      ? String(aVal).localeCompare(String(bVal))
      : String(bVal).localeCompare(String(aVal));
  });

  const displayed = expanded ? sorted : sorted.slice(0, 5);

  const toggleSort = (key: keyof BtTrade) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const SortIcon = ({ col }: { col: keyof BtTrade }) => (
    <span className="inline-flex flex-col ml-1 opacity-40">
      <ChevronUp size={7} className={sortKey === col && sortDir === 'asc' ? 'opacity-100 text-primary' : ''} />
      <ChevronDown size={7} className={sortKey === col && sortDir === 'desc' ? 'opacity-100 text-primary' : ''} />
    </span>
  );

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3.5 border-b border-border">
        <div>
          <h4 className="text-sm font-600 text-foreground">Simulated Trades</h4>
          <p className="text-xs text-muted-foreground mt-0.5">88 total trades generated by backtest</p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="bg-positive-subtle text-positive border border-positive/20 px-2 py-1 rounded-full font-500">54 wins</span>
          <span className="bg-negative-subtle text-negative border border-negative/20 px-2 py-1 rounded-full font-500">34 losses</span>
        </div>
      </div>

      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              {[
                { key: 'date', label: 'Date' },
                { key: 'direction', label: 'Dir' },
                { key: 'entry', label: 'Entry' },
                { key: 'exit', label: 'Exit' },
                { key: 'pnl', label: 'P&L' },
                { key: 'rr', label: 'R:R' },
                { key: 'patternScore', label: 'Match %' },
                { key: 'bars', label: 'Bars Held' },
                { key: 'outcome', label: 'Outcome' },
              ].map((col) => (
                <th
                  key={`bt-th-${col.key}`}
                  onClick={() => toggleSort(col.key as keyof BtTrade)}
                  className="px-3 py-3 text-left text-xs font-500 text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-colors whitespace-nowrap select-none"
                >
                  {col.label}
                  <SortIcon col={col.key as keyof BtTrade} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayed.map((trade, idx) => (
              <tr
                key={trade.id}
                className={`border-b border-border/40 transition-colors hover:bg-muted/30 ${
                  idx % 2 === 0 ? 'bg-transparent' : 'bg-muted/10'
                }`}
              >
                <td className="px-3 py-2.5 text-xs text-muted-foreground font-mono-nums whitespace-nowrap">{trade.date}</td>
                <td className="px-3 py-2.5">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-600 ${
                    trade.direction === 'Long' ? 'bg-positive-subtle text-positive' : 'bg-negative-subtle text-negative'
                  }`}>
                    {trade.direction}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-xs text-foreground font-mono-nums">${trade.entry.toFixed(2)}</td>
                <td className="px-3 py-2.5 text-xs text-foreground font-mono-nums">${trade.exit.toFixed(2)}</td>
                <td className="px-3 py-2.5">
                  <span className={`text-sm font-600 font-mono-nums ${trade.pnl >= 0 ? 'text-positive' : 'text-negative'}`}>
                    {trade.pnl >= 0 ? '+' : ''}${trade.pnl.toLocaleString()}
                  </span>
                </td>
                <td className="px-3 py-2.5">
                  <span className={`text-xs font-500 font-mono-nums ${trade.rr >= 1.5 ? 'text-positive' : trade.rr >= 0 ? 'text-warning' : 'text-negative'}`}>
                    {trade.rr >= 0 ? `1:${trade.rr.toFixed(1)}` : trade.rr.toFixed(1)}
                  </span>
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full"
                        style={{ width: `${trade.patternScore}%` }}
                      />
                    </div>
                    <span className="text-xs font-mono-nums text-foreground">{trade.patternScore}%</span>
                  </div>
                </td>
                <td className="px-3 py-2.5 text-xs text-muted-foreground font-mono-nums">{trade.bars} bars</td>
                <td className="px-3 py-2.5">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-500 ${
                    trade.outcome === 'Win' ?'bg-positive-subtle text-positive border border-positive/20' :'bg-negative-subtle text-negative border border-negative/20'
                  }`}>
                    {trade.outcome}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="px-4 py-3 border-t border-border text-center">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1.5 mx-auto text-xs text-primary hover:text-primary/80 font-500 transition-colors"
        >
          {expanded ? (
            <>
              <ChevronUp size={13} />
              Show less
            </>
          ) : (
            <>
              <ChevronDown size={13} />
              Show all 88 simulated trades
            </>
          )}
        </button>
      </div>
    </div>
  );
}